import { React, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalStateContext } from '../utils/GlobalStateContext';
import { AuthContext } from '../utils/AuthenticationCheck';
import { Shield, ShieldCheck, Copy, CheckCircle } from 'lucide-react';
import '../style/Login.css';
import '../style/Mfa.css';
import health_guardian from '../img/health_guardian.webp';

const ROLE_ROUTES = {
  rad_tech:         '/radiology',
  lab_tech:         '/labs',
  office_assistant: '/checkIn',
  clinician:        '/home',
  medical_assistant:'/home',
  biller:           '/billing',
  admin:            '/home',
};

export default function MfaEnforceSetup() {
  const navigate = useNavigate();
  const { currentUser, setCurrentUser } = useContext(GlobalStateContext);
  const { user } = useContext(AuthContext);
  const BASE_URL = process.env.REACT_APP_API_URL;

  const [step, setStep] = useState('intro');   // 'intro' | 'qr' | 'confirm' | 'done'
  const [qrCode, setQrCode]   = useState('');
  const [secret, setSecret]   = useState('');
  const [code, setCode]       = useState('');
  const [copied, setCopied]   = useState(false);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const handleBegin = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/mfa/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user?.uid, email: user?.email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Setup failed. Please try again.'); return; }
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setStep('qr');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/mfa/confirm-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user?.uid, email: user?.email, code }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Invalid code. Please try again.'); return; }
      setCurrentUser({ ...currentUser, mfa_enabled: true });
      setStep('done');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const proceedToDashboard = () => {
    navigate(ROLE_ROUTES[currentUser?.type] || '/home');
  };

  // ── Intro ──────────────────────────────────────────────────────────────
  if (step === 'intro') {
    return (
      <div className="login-page">
        <div className="login-bg-image" />
        <div className="login-card">
          <div className="login-header">
            <div className="mfa-shield-wrap">
              <Shield size={44} color="white" />
            </div>
            <h1 className="login-title">MFA Required</h1>
            <p className="login-subtitle">
              Your account requires two-factor authentication. Set up an authenticator app to continue.
            </p>
          </div>
          <div className="login-form">
            {error && <p className="mfa-error">{error}</p>}
            <p style={{ fontSize: 14, color: '#495057', margin: 0, lineHeight: 1.6 }}>
              You'll need an authenticator app such as <strong>Google Authenticator</strong> or <strong>Authy</strong>.
              This takes about 2 minutes.
            </p>
            <button className="login-submit-btn" onClick={handleBegin} disabled={loading}>
              <Shield size={18} />
              <span>{loading ? 'Loading…' : 'Begin Setup'}</span>
            </button>
          </div>
          <div className="login-footer"><span>Powered by Health Direct Plus</span></div>
        </div>
      </div>
    );
  }

  // ── QR code ────────────────────────────────────────────────────────────
  if (step === 'qr') {
    return (
      <div className="login-page">
        <div className="login-bg-image" />
        <div className="login-card" style={{ maxWidth: 480 }}>
          <div className="login-header">
            <div className="login-logo-wrap">
              <img src={health_guardian} alt="Logo" className="login-logo" />
            </div>
            <h1 className="login-title">Scan QR Code</h1>
            <p className="login-subtitle">Open your authenticator app and scan the code below.</p>
          </div>
          <div className="login-form">
            <div className="mfa-qr-wrap">
              <img src={qrCode} alt="MFA QR Code" className="mfa-qr-img" />
            </div>
            <p className="mfa-manual-label">Or enter this key manually:</p>
            <div className="mfa-secret-row">
              <code className="mfa-secret-code">{secret}</code>
              <button type="button" className="mfa-copy-btn" onClick={copySecret}>
                {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <button className="login-submit-btn" onClick={() => { setStep('confirm'); setError(''); }}>
              <Shield size={18} /><span>Next — Enter Code</span>
            </button>
          </div>
          <div className="login-footer"><span>Powered by Health Direct Plus</span></div>
        </div>
      </div>
    );
  }

  // ── Confirm ────────────────────────────────────────────────────────────
  if (step === 'confirm') {
    return (
      <div className="login-page">
        <div className="login-bg-image" />
        <div className="login-card">
          <div className="login-header">
            <div className="mfa-shield-wrap">
              <Shield size={44} color="white" />
            </div>
            <h1 className="login-title">Confirm Setup</h1>
            <p className="login-subtitle">Enter the 6-digit code your authenticator app is showing now.</p>
          </div>
          <form className="login-form" onSubmit={handleConfirm}>
            <div className="mfa-code-wrap">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000 000"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                className="mfa-code-input"
                autoFocus
              />
            </div>
            {error && <p className="mfa-error">{error}</p>}
            <button type="submit" className="login-submit-btn" disabled={loading || code.length !== 6}>
              <Shield size={18} /><span>{loading ? 'Confirming…' : 'Confirm & Continue'}</span>
            </button>
            <button type="button" className="mfa-back-btn" onClick={() => setStep('qr')}>
              Back
            </button>
          </form>
          <div className="login-footer"><span>Powered by Health Direct Plus</span></div>
        </div>
      </div>
    );
  }

  // ── Done ───────────────────────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-bg-image" />
      <div className="login-card">
        <div className="login-header">
          <div className="mfa-shield-wrap" style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}>
            <ShieldCheck size={44} color="white" />
          </div>
          <h1 className="login-title">You're Protected</h1>
          <p className="login-subtitle">Two-factor authentication is now active on your account.</p>
        </div>
        <div className="login-form">
          <button className="login-submit-btn" onClick={proceedToDashboard}>
            <ShieldCheck size={18} /><span>Continue to Dashboard</span>
          </button>
        </div>
        <div className="login-footer"><span>Powered by Health Direct Plus</span></div>
      </div>
    </div>
  );
}
